import { getPublicityByID } from "../modules/Enumeration";
import Poll from "./Poll";

class Is {
    constructor({
        liked = false,
        saved = false,
        shared = false,
        reported = false,
    } = {}) {
        this.liked = liked;
        this.saved = saved;
        this.shared = shared;
        this.reported = reported;
    }

    static fromAPI(data) {
        if (!data) return new Is();
        return new Is({
            liked: data.liked || false,
            saved: data.saved || false,
            shared: data.shared || false,
            reported: data.reported || false,
        });
    }

    toJSON() {
        return {
            liked: this.liked,
            saved: this.saved,
            shared: this.shared,
            reported: this.reported,
        };
    }
}

class Post {
    constructor({
        id = null,
        userId = null,
        caption = '',
        media = [],
        background = 'none',
        publicity = 'anyone',
        type = 'text',
        displayLayout = 'classic',
        flairs = [],
        likeCount = 0,
        shareCount = 0,
        commentCount = 0,
        createdAt = null,
        updatedAt = null,
        archivedAt = null,
        author = null,
        taggedUsers = null,
        sharedPost = null,
        poll = null,
        figures = [],
        figureDatabaseEntry = null,
        gifUrl = null,
        group = null,
        isPinned = false,
        company = null,
        rating = 'sfw',
        embeddedUrls = null,
        is = {
            liked: false,
            saved: false,
            shared: false,
            reported: false
        }
    } = {}) {
        this.id = id || this.generateId();
        this.userId = userId;
        this.caption = caption;
        this.media = media;
        this.background = background;
        this.publicity = publicity;
        this.type = type;
        this.displayLayout = displayLayout;
        this.flairs = flairs;
        this.likeCount = likeCount;
        this.shareCount = shareCount;
        this.commentCount = commentCount;
        this.createdAt = createdAt ? new Date(createdAt) : new Date();
        this.updatedAt = updatedAt ? new Date(updatedAt) : new Date();
        this.archivedAt = archivedAt ? new Date(archivedAt) : null;
        this.author = author;
        this.taggedUsers = taggedUsers;
        this.sharedPost = sharedPost;
        this.poll = poll;
        this.figures = figures;
        this.figureDatabaseEntry = figureDatabaseEntry;
        this.embeddedUrls = embeddedUrls;
        this.gifUrl = gifUrl;
        this.group = group;
        this.isPinned = isPinned || false;
        this.company = company;
        this.rating = rating || 'sfw';
        this.is = new Is(is);

        this.publicityIcon = null;
    }

    static fromAPI(data) {  
        if (!data) return null;
        
        try {
            // Handle both camelCase and snake_case with prefixes (a_, b_, etc.)
            const id = data.id || data.a_id;
            const caption = data.caption || data.a_caption;
            const publicity = data.publicity || data.a_publicity || 'anyone';
            const type = data.type || data.a_type || 'text';
            const rating = data.rating || data.a_rating;
            const flairs = data.flairs || data.a_flairs || [];
            const displayLayout = data.displayLayout || data.display_layout || 'classic';
            const likeCount = data.likeCount || data.like_count || 0;
            const shareCount = data.shareCount || data.share_count || 0;
            const commentCount = data.commentCount || data.comment_count || 0;
            const createdAt = data.createdAt || data.created_at;
            const updatedAt = data.updatedAt || data.updated_at;
            const archivedAt = data.archivedAt || data.archived_at;
            const userId = data.userId || data.author_id || data.authorId;
            const gifUrl = data.gifUrl || data.gif_url;
            
            // Parse background safely
            let background = null;
            const bgData = data.background || data.a_background;
            if (bgData && bgData !== 'none') {
                if (typeof bgData === 'string') {
                    try {
                        background = JSON.parse(bgData);
                    } catch (e) {
                        console.warn('Failed to parse background:', bgData);
                        background = null;
                    }
                } else {
                    background = bgData;
                }
            }
            
            // Handle author data
            let author = data.author;
            if (!author && data.b_username) {
                author = {
                    id: userId,
                    username: data.b_username,
                    displayName: data.display_name,
                    avatarUrl: data.author?.avatarUrl
                };
            }
            
            // Handle is object
            const is = data.is || {
                liked: data.isLiked || false,
                saved: data.isSaved || false,
                shared: data.isShared || false,
                reported: false
            };
            
            return new Post({
                id,
                userId,
                caption,
                media: data.media || [],
                background,
                publicity,
                type,
                displayLayout,
                flairs,
                likeCount,
                shareCount,
                commentCount,
                createdAt,
                updatedAt,
                archivedAt,
                author,
                taggedUsers: data.taggedUsers || data.tagged_users || null,
                sharedPost: data.sharedPost ? Post.fromAPI(data.sharedPost) : null,
                poll: data.poll ? Poll.fromAPI(data.poll) : null,
                figures: data.figures || data.postFigures || [],
                figureDatabaseEntry: data.figureDatabaseEntry || null,
                embeddedUrls: data.embeddedUrls || data.embedded_urls || null,
                gifUrl,
                group: data.group || null,
                isPinned: data.isPinned || false,
                company: data.company || null,
                rating: data.rating || data.a_rating || 'sfw',
                is
            });
        } catch (error) {
            console.error('Error parsing post from API:', error, data);
            return null;
        }
    }

    toJSON() {
        return {
            id: this.id,
            userId: this.userId,
            caption: this.caption,
            media: this.media,
            background: this.background ? JSON.stringify(this.background) : 'none',
            publicity: this.publicity,
            type: this.type,
            displayLayout: this.displayLayout,
            flairs: this.flairs,
            likeCount: this.likeCount,
            shareCount: this.shareCount,
            commentCount: this.commentCount,
            createdAt: this.createdAt,
            updatedAt: this.updatedAt,
            author: this.author,
            taggedUsers: this.taggedUsers,
            sharedPost: this.sharedPost ? this.sharedPost.toJSON() : null,
            poll: this.poll ? this.poll.toJSON() : null,
            group: this.group,
            isPinned: this.isPinned,
            company: this.company,
            is: this.is.toJSON(),
            publicityIcon: this.publicityIcon
        };
    }
}

class PostComment {
    constructor({
        id = null,
        postId = null,
        userId = null,
        comment = '',
        createdAt = null,
        updatedAt = null,
        is = {}
    } = {}) {
        this.id = id;
        this.postId = postId;
        this.userId = userId;
        this.comment = comment;
        this.createdAt = createdAt ? new Date(createdAt) : new Date();
        this.updatedAt = updatedAt ? new Date(updatedAt) : new Date();
        this.is = new Is(is);
    }

    static fromAPI(data) {
        if (!data) return null;
        return new PostComment({
            id: data.id,
            postId: data.postId,
            userId: data.userId,
            comment: data.comment,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            is: data.is || {}
        });
    }

    toJSON() {
        return {
            id: this.id,
            postId: this.postId,
            userId: this.userId,
            comment: this.comment,
            createdAt: this.createdAt,
            updatedAt: this.updatedAt,
            is: this.is.toJSON()
        };
    }
}

export default Post;